// Utilisation à la télécommande.
//
// Les télés et boîtiers envoient leurs touches directionnelles comme des
// événements clavier ordinaires : il n'y a donc rien de spécial à faire pour
// les recevoir, mais un navigateur ne sait pas déplacer le focus « vers le
// bas ». On le fait ici, géométriquement, ce qui sert aussi au clavier sur un
// poste normal.

const FOCUSABLE = 'button:not([disabled]), select, input, video, [tabindex="0"]';

type Direction = "up" | "down" | "left" | "right";

const DIRECTIONS: Record<string, Direction> = {
  ArrowUp: "up",
  ArrowDown: "down",
  ArrowLeft: "left",
  ArrowRight: "right",
};

function targets(): HTMLElement[] {
  return [...document.querySelectorAll<HTMLElement>(FOCUSABLE)].filter((el) => {
    const rect = el.getBoundingClientRect();
    return rect.width > 0 && rect.height > 0;
  });
}

/** Cherche l'élément le plus proche dans la direction demandée : on additionne
 *  la distance sur l'axe visé et l'écart latéral, ce dernier pesant double
 *  pour que le focus suive les colonnes et les lignes. */
function bestInDirection(from: HTMLElement, direction: Direction): HTMLElement | null {
  const origin = from.getBoundingClientRect();

  let best: HTMLElement | null = null;
  let bestScore = Number.POSITIVE_INFINITY;

  for (const el of targets()) {
    if (el === from) continue;

    const rect = el.getBoundingClientRect();

    const main =
      direction === "down"
        ? rect.top - origin.bottom
        : direction === "up"
          ? origin.top - rect.bottom
          : direction === "right"
            ? rect.left - origin.right
            : origin.left - rect.right;

    // un léger chevauchement reste acceptable, un élément derrière soi non
    const tolerance =
      direction === "up" || direction === "down"
        ? Math.min(origin.height, rect.height) / 2
        : Math.min(origin.width, rect.width) / 2;

    if (main < -tolerance) continue;

    const cross =
      direction === "up" || direction === "down"
        ? Math.abs((rect.left + rect.right) / 2 - (origin.left + origin.right) / 2)
        : Math.abs((rect.top + rect.bottom) / 2 - (origin.top + origin.bottom) / 2);

    const score = Math.max(main, 0) + cross * 2;

    if (score < bestScore) {
      bestScore = score;
      best = el;
    }
  }

  return best;
}

export interface TvOptions {
  /** Retour arrière : touche Retour de la télécommande, Échap au clavier. */
  onBack: () => void;
}

export function install(options: TvOptions): void {
  document.addEventListener("keydown", (event) => {
    if (event.altKey || event.ctrlKey || event.metaKey) return;

    const active = document.activeElement as HTMLElement | null;
    const tag = active?.tagName ?? "";

    if (event.key === "Escape" || (event.key === "Backspace" && tag !== "INPUT")) {
      event.preventDefault();
      options.onBack();

      return;
    }

    if (event.key === "Enter" && tag === "VIDEO") {
      const video = active as HTMLVideoElement;

      event.preventDefault();
      if (video.paused) void video.play().catch(() => undefined);
      else video.pause();

      return;
    }

    const direction = DIRECTIONS[event.key];
    if (!direction) return;

    // dans une liste déroulante, les flèches choisissent l'option
    if (tag === "SELECT") return;

    const horizontal = direction === "left" || direction === "right";

    // dans un champ texte, gauche/droite déplacent le curseur ;
    // sur la vidéo, elles avancent et reculent
    if (horizontal && (tag === "INPUT" || tag === "VIDEO")) return;

    const from = active && active !== document.body ? active : targets()[0];
    if (!from) return;

    const next = bestInDirection(from, direction);
    if (!next) return;

    event.preventDefault();
    next.focus();
    next.scrollIntoView({ block: "nearest", inline: "nearest" });
  });
}

const KEY = "aniweb-tv";

export function setMode(on: boolean): void {
  if (on) document.documentElement.dataset["tv"] = "1";
  else delete document.documentElement.dataset["tv"];

  try {
    localStorage.setItem(KEY, on ? "1" : "0");
  } catch {
    // stockage indisponible : le mode vaudra pour cette session seulement
  }
}

export function isMode(): boolean {
  return document.documentElement.dataset["tv"] === "1";
}

/** Le mode se retient d'une fois sur l'autre ; `?tv` dans l'URL permet d'en
 *  faire un favori sur la télé sans avoir à le réactiver. */
export function restoreMode(): void {
  if (new URLSearchParams(location.search).has("tv")) {
    setMode(true);
    return;
  }

  try {
    setMode(localStorage.getItem(KEY) === "1");
  } catch {
    setMode(false);
  }
}
